import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { currentUser, isDomainAdmin, logout } from "../pages/login/loginSlice";
import "./AccountMenu.scss";

function getInitials(user: string): string {
    const localPart = user.split("@")[0] || "";
    const parts = localPart.split(/[._+-]+/).filter(Boolean);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    return "?";
}

function AccountMenu() {
    const dispatch = useAppDispatch();
    const user = useAppSelector(currentUser);
    const userIsDomainAdmin = useAppSelector(isDomainAdmin);
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const closeMenu = (returnFocus: boolean) => {
        setIsOpen(false);
        if (returnFocus) {
            buttonRef.current?.focus();
        }
    };

    useEffect(() => {
        if (!isOpen) return;

        const getItems = () => Array.from(
            containerRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])') ?? []
        );

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                closeMenu(true);
                return;
            }
            const items = getItems();
            const currentIndex = items.indexOf(document.activeElement as HTMLElement);
            if (e.key === "ArrowDown") {
                e.preventDefault();
                items[(currentIndex + 1) % items.length]?.focus();
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                items[(currentIndex - 1 + items.length) % items.length]?.focus();
            } else if (e.key === "Home") {
                e.preventDefault();
                items[0]?.focus();
            } else if (e.key === "End") {
                e.preventDefault();
                items[items.length - 1]?.focus();
            }
        };

        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                closeMenu(false);
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            containerRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
        }
    }, [isOpen]);

    if (!user) {
        return null;
    }

    return (
        <div className={"accountMenu"} ref={containerRef}>
            <button
                type="button"
                ref={buttonRef}
                className={"accountMenuTrigger"}
                aria-haspopup="true"
                aria-expanded={isOpen}
                aria-label={`Account menu for ${user}`}
                onClick={() => setIsOpen((open) => !open)}
                onKeyDown={(e) => {
                    if (e.key === "ArrowDown" && !isOpen) {
                        e.preventDefault();
                        setIsOpen(true);
                    }
                }}
            >
                {getInitials(user)}
            </button>
            {isOpen && (
                <div className={"accountMenuDropdown"} role="menu" aria-label="Account">
                    <div className={"accountMenuUser"}>{user}</div>
                    <Link to="/account" role="menuitem" className={"accountMenuItem"} onClick={() => closeMenu(false)}>
                        Account settings
                    </Link>
                    {userIsDomainAdmin && (
                        <Link to="/account/domain" role="menuitem" className={"accountMenuItem"} onClick={() => closeMenu(false)}>
                            Domain settings
                        </Link>
                    )}
                    {/* Placeholder until multi-session support lands in the redux store (separate frontend task) */}
                    <button type="button" role="menuitem" className={"accountMenuItem"} aria-disabled="true" aria-label="Switch account (coming soon)" disabled>
                        Switch account
                    </button>
                    <button
                        type="button"
                        role="menuitem"
                        className={"accountMenuItem logoutItem"}
                        onClick={() => {
                            closeMenu(false);
                            dispatch(logout());
                        }}
                    >
                        Logout
                    </button>
                </div>
            )}
        </div>
    );
}

export default AccountMenu;
